import productionchargeService from '../../../services/productionchargeService';
import filterService from '../../../services/filterService';

import {
    setProductionChargeLoading,
    setProductionChargeError,
    setProductionChargeList,
    setProductionChargeCurrent,
    setProductionChargePagination,
    setProductionChargeSort,
    setProductionChargeFilters,
    setProductionChargeDefaultFilter,
    setProductionChargeSettings,
    clearProductionChargeCurrent,
    clearProductionChargeError,
    setProductionChargeFilterLoading,
    changeProductionChargeFilterCondition,
    addProductionChargeFilterCondition,
    updateProductionChargeFilterConditionRule,
    updateProductionChargeFilterConditionValue,
    removeProductionChargeFilterCondition,
    setProductionChargeSelected,
    resetProductionChargeFiltersAndPagination
} from './productionchargeSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().productioncharge.isSettingsInitialized) {
            dispatch(setProductionChargeLoading(true));
            const response = await productionchargeService.getSettings();

            // Set the settings
            dispatch(setProductionChargeSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setProductionChargeDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setProductionChargeError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setProductionChargeLoading(false));
    }
};

export const fetchProductioncharges = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().productioncharge;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setProductionChargeLoading(true));
        const response = await productionchargeService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setProductionChargeList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchProductioncharges:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setProductionChargeError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setProductionChargeLoading(false));
    }
};

export const fetchProductionCharge = (id) => async (dispatch) => {
    try {
        dispatch(setProductionChargeLoading(true));
        const response = await productionchargeService.getOne(id);
        dispatch(setProductionChargeCurrent(response.data));
    } catch (error) {
        dispatch(setProductionChargeError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setProductionChargeLoading(false));
    }
};

export const createProductionCharge = (data) => async (dispatch) => {
    try {
        const response = await productionchargeService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateProductionCharge = (id, data) => async (dispatch) => {
    try {
        const response = await productionchargeService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteProductionCharge = (id) => async (dispatch) => {
    try {
        dispatch(setProductionChargeLoading(true));
        const response = await productionchargeService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setProductionChargeError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setProductionChargeLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setProductionChargePagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setProductionChargeSort(sort));
};

export const searchProductioncharges = (query) => (dispatch, getState) => {
    const currentFilters = getState().productioncharge.filters;
    dispatch(setProductionChargeFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setProductionChargeDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeProductionChargeFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addProductionChargeFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateProductionChargeFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateProductionChargeFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeProductionChargeFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setProductionChargeFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().productioncharge.settings;
            dispatch(setProductionChargeSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setProductionChargeFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setProductionChargeFilterLoading(false));
        dispatch(setProductionChargeError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setProductionChargeSelected(selectedRows));
    };
};

export const clearCurrentProductionCharge = () => {
    return (dispatch) => {
        dispatch(clearProductionChargeCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetProductionChargeFiltersAndPagination());
};